import type { Medicine } from "@/entities/medicine/api/medicines";
import { translateDosageForm } from "@/entities/medicine/lib/medicine-label";
import type { VariantAttribute } from "@/shared/config/constants";

export type ResultAttribute = VariantAttribute | "vendor_name";

export type GroupedFinderResult = {
  label: string;
  items: Medicine[];
  internationalName?: string | null;
  tradeNameHighlight?: string | null;
  nameHighlight?: string | null;
  labelHighlight?: string | null;
  shared: Partial<Record<VariantAttribute, Medicine[keyof Medicine]>>;
  visibleAttributes: ResultAttribute[];
};

/**
 * Returns the Typesense highlight snippet that matches a medicine's display name
 * (`trade_name || name`).
 */
export function getMedicineNameHighlight(medicine: Medicine): string | null {
  if (medicine.trade_name) return medicine.highlights?.trade_name ?? null;
  return medicine.highlights?.name ?? null;
}

export function buildGroupedFinderResults(finderResults: Medicine[]): GroupedFinderResult[] {
  const groups = new Map<string, Medicine[]>();

  for (const medicine of finderResults) {
    const key = medicine.trade_name || medicine.name;
    const currentGroup = groups.get(key) ?? [];
    currentGroup.push(medicine);
    groups.set(key, currentGroup);
  }

  return [...groups.entries()].map(([label, items]) => {
    const shared: Partial<Record<VariantAttribute, Medicine[keyof Medicine]>> = {};
    const varying = new Set<ResultAttribute>();
    const sharedAttributes: VariantAttribute[] = [
      "dosage_form",
      "strength",
      "volume",
      "container_type",
      "pack",
      "vendor_country",
    ];
    const comparableAttributes: ResultAttribute[] = [...sharedAttributes, "vendor_name"];

    for (const attribute of comparableAttributes) {
      const values = new Set(items.map((item) => item[attribute] ?? null));
      if (values.size > 1) {
        varying.add(attribute);
        continue;
      }

      const [value] = values;
      if (value !== null && sharedAttributes.includes(attribute as VariantAttribute)) {
        shared[attribute as VariantAttribute] = value;
      }
    }

    const visibleAttributes =
      items.length === 1
        ? (["dosage_form", "strength", "volume", "pack", "vendor_country"] as ResultAttribute[]).filter(
            (attribute) => items[0][attribute] != null,
          )
        : ([...sharedAttributes, "vendor_name"] as ResultAttribute[]).filter((attribute) => varying.has(attribute));

    items.sort((left, right) => {
      for (const attribute of visibleAttributes) {
        const leftValue = String(left[attribute] ?? "");
        const rightValue = String(right[attribute] ?? "");
        const comparison = leftValue.localeCompare(rightValue, undefined, { numeric: true });
        if (comparison !== 0) {
          return comparison;
        }
      }

      return left.name.localeCompare(right.name);
    });

    const firstItem = items[0];
    const tradeNameHighlight = firstItem?.highlights?.trade_name ?? null;
    const nameHighlight = firstItem?.highlights?.name ?? null;
    const labelHighlight = firstItem ? getMedicineNameHighlight(firstItem) : null;

    return {
      label,
      items,
      shared,
      visibleAttributes,
      tradeNameHighlight,
      nameHighlight,
      labelHighlight,
      internationalName: firstItem?.international_name_ru ?? firstItem?.international_name ?? null,
    };
  });
}

export function formatVariantSpecs(medicine: Medicine, group: GroupedFinderResult): string {
  const parts: string[] = [];
  for (const attribute of group.visibleAttributes) {
    const value = medicine[attribute as keyof Medicine];
    if (value == null || value === "") continue;
    if (attribute === "dosage_form") {
      parts.push(translateDosageForm(String(value)) ?? String(value));
    } else {
      parts.push(String(value));
    }
  }
  return parts.join(" · ");
}
